import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import { ArrowLeft, RotateCcw, Save, X } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useListCarrierAddOnServices,
  useUpdateCarrierAddOnService,
  type UpdateCarrierAddOnServiceInput,
  type SignatureServiceInput,
} from "@/api/carrier-add-on-services";
import { useGetCarriers } from "@/api/carriers";
import type { CarrierAddOnService } from "@/api.types.v2";
import { ServiceEnablementPanel } from "./service-enablement-panel";

/**
 * Tabbed detail view for a single SIGNATURE add-on service. Three tabs:
 * Overview (code/name/active), Pricing (flat fee only — no percentage, no
 * clamps), Enablement (reuses ServiceEnablementPanel with type=SIGNATURE).
 * Mirrors PackagingServiceDetailScreen's shape with a simpler Pricing tab.
 */
export function SignatureServiceDetailScreen() {
  const { id } = useParams<{ id: string }>();

  const { data: services, isLoading: servicesLoading } =
    useListCarrierAddOnServices({ type: "SIGNATURE" });
  const { data: carriersResp } = useGetCarriers();

  const service = id ? services?.find((s) => s.id === id) : undefined;
  const providerName =
    carriersResp?.data.find((c) => c.id === service?.providerCarrierId)?.name ??
    "—";

  if (servicesLoading) {
    return (
      <div className="p-4 space-y-4">
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="p-4 space-y-4">
        <div className="text-center p-8 text-muted-foreground">
          <p>Signature service not found.</p>
          <Button asChild variant="link" size="sm" className="mt-2">
            <Link to="/add-on-services/signature">
              <ArrowLeft className="h-3 w-3 mr-1" />
              Back to Signature Services
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <DetailHeader service={service} providerName={providerName} />

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="pricing">Pricing</TabsTrigger>
          <TabsTrigger value="enablement">Enablement</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab service={service} />
        </TabsContent>
        <TabsContent value="pricing">
          <PricingTab service={service} />
        </TabsContent>
        <TabsContent value="enablement">
          <ServiceEnablementPanel serviceId={service.id} type="SIGNATURE" />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function DetailHeader({
  service,
  providerName,
}: {
  service: CarrierAddOnService;
  providerName: string;
}) {
  return (
    <div className="space-y-2">
      <div className="text-sm">
        <Link
          to="/add-on-services/signature"
          className="text-muted-foreground hover:text-foreground inline-flex items-center"
        >
          <ArrowLeft className="h-3 w-3 mr-1" />
          Signature Services
        </Link>
      </div>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-3xl font-bold">{service.name}</h1>
          <code className="font-mono text-sm text-muted-foreground">
            {service.code}
          </code>
          {service.active ? (
            <Badge
              variant="secondary"
              className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100"
            >
              Active
            </Badge>
          ) : (
            <Badge
              variant="secondary"
              className="bg-zinc-100 text-zinc-700 hover:bg-zinc-100"
            >
              Inactive
            </Badge>
          )}
        </div>
        <div className="text-sm text-muted-foreground whitespace-nowrap">
          Provider: <span className="text-foreground">{providerName}</span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers — dollars↔cents conversion + dirty-check parsing. Same approach as
// packaging / insurance detail screens.
// ---------------------------------------------------------------------------

function parseDollarsToCents(s: string): number | null | undefined {
  const trimmed = s.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return Math.round(n * 100);
}

function centsToDollarString(cents: number | null | undefined): string {
  if (cents == null) return "";
  return String(cents / 100);
}

// ---------------------------------------------------------------------------
// Overview tab — code (RO), name, active. No signature-specific Overview fields.
// ---------------------------------------------------------------------------
function OverviewTab({ service }: { service: CarrierAddOnService }) {
  const [name, setName] = useState(service.name);
  const [active, setActive] = useState(service.active);

  // Re-seed only on :id change — same pattern as packaging Overview tab.
  const serviceRef = useRef(service);
  serviceRef.current = service;
  useEffect(() => {
    const s = serviceRef.current;
    setName(s.name);
    setActive(s.active);
  }, [service.id]);

  const update = useUpdateCarrierAddOnService();

  const buildPatch = (): UpdateCarrierAddOnServiceInput | null => {
    const patch: UpdateCarrierAddOnServiceInput = {};
    const trimmedName = name.trim();
    if (trimmedName && trimmedName !== service.name) patch.name = trimmedName;
    if (active !== service.active) patch.active = active;
    return Object.keys(patch).length === 0 ? null : patch;
  };

  const hasChanges = (() => {
    if (name.trim() && name.trim() !== service.name) return true;
    if (active !== service.active) return true;
    return false;
  })();

  const handleSave = () => {
    const patch = buildPatch();
    if (patch === null) {
      toast.message("Nothing to update.");
      return;
    }
    update.mutate(
      { id: service.id, data: patch },
      {
        onSuccess: (updated) => {
          toast.success("Signature service updated.");
          setName(updated.name);
          setActive(updated.active);
        },
        onError: (err: unknown) => {
          const e = err as { response?: { data?: { error?: string } } };
          toast.error(e.response?.data?.error ?? "Failed to update service.");
        },
      }
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Overview</CardTitle>
        <p className="text-sm text-muted-foreground">
          Code <code className="font-mono">{service.code}</code> and provider
          carrier can&apos;t be changed. To retire, toggle off the Active
          switch.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1">
          <Label htmlFor="sg-detail-code">Code</Label>
          <Input
            id="sg-detail-code"
            value={service.code}
            readOnly
            disabled
            className="font-mono"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="sg-detail-name">Name</Label>
          <Input
            id="sg-detail-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="flex items-center justify-between rounded-md border p-3">
          <div>
            <Label className="text-sm font-medium">Active</Label>
            <p className="text-xs text-muted-foreground">
              Inactive services are hidden from booking flows but preserved for
              existing references.
            </p>
          </div>
          <Button
            variant={active ? "outline" : "default"}
            size="sm"
            onClick={() => setActive(!active)}
          >
            {active ? (
              <>
                <X className="h-3 w-3 mr-1" />
                Deactivate
              </>
            ) : (
              <>
                <RotateCcw className="h-3 w-3 mr-1" />
                Reactivate
              </>
            )}
          </Button>
        </div>
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={update.isPending || !hasChanges}
          >
            <Save className="h-4 w-4 mr-1" />
            {update.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Pricing tab — flat fee only. Server upserts the details row; we always
// send a `signature:` block with the changed value (or null to clear).
// ---------------------------------------------------------------------------
function PricingTab({ service }: { service: CarrierAddOnService }) {
  const initialFlat = service.signatureDetails?.flatFeeCents ?? null;

  const [flatFeeDollars, setFlatFeeDollars] = useState(
    centsToDollarString(initialFlat)
  );

  // Re-seed on :id change.
  const serviceRef = useRef(service);
  serviceRef.current = service;
  useEffect(() => {
    const d = serviceRef.current.signatureDetails;
    setFlatFeeDollars(centsToDollarString(d?.flatFeeCents ?? null));
  }, [service.id]);

  const update = useUpdateCarrierAddOnService();

  // Build the `signature:` block. Returns:
  //   undefined → invalid input (toast already fired)
  //   null      → nothing to send
  //   object    → signature block to send
  const buildSignature = (): SignatureServiceInput | null | undefined => {
    const flat = parseDollarsToCents(flatFeeDollars);
    if (flat === undefined) {
      toast.error("Flat fee must be a non-negative number or blank.");
      return undefined;
    }
    if (flat === initialFlat) return null;
    return { flatFeeCents: flat };
  };

  const hasChanges = (() => {
    const flat = parseDollarsToCents(flatFeeDollars);
    if (flat === undefined) return true;
    if (flat !== initialFlat) return true;
    return false;
  })();

  const handleSave = () => {
    const signature = buildSignature();
    if (signature === undefined) return;
    if (signature === null) {
      toast.message("Nothing to update.");
      return;
    }
    update.mutate(
      { id: service.id, data: { signature } },
      {
        onSuccess: (updated) => {
          toast.success("Pricing updated.");
          const d = updated.signatureDetails;
          setFlatFeeDollars(centsToDollarString(d?.flatFeeCents ?? null));
        },
        onError: (err: unknown) => {
          const e = err as { response?: { data?: { error?: string } } };
          toast.error(e.response?.data?.error ?? "Failed to update pricing.");
        },
      }
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pricing</CardTitle>
        <p className="text-sm text-muted-foreground">
          A single flat fee charged when this signature is requested.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1">
          <Label htmlFor="sgp-flat">Flat fee (CAD)</Label>
          <Input
            id="sgp-flat"
            type="number"
            step="any"
            value={flatFeeDollars}
            onChange={(e) => setFlatFeeDollars(e.target.value)}
            placeholder="—"
          />
          <p className="text-xs text-muted-foreground">Leave blank to clear.</p>
        </div>

        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={update.isPending || !hasChanges}
          >
            <Save className="h-4 w-4 mr-1" />
            {update.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
